import { PermissionGuard } from '@/components/PermissionGuard';
import { TravelEsimQrCodeDialog } from '@/components/TravelEsimQrCodeDialog';
import { useResendTravelEsimQrCode } from '@/hooks/useTravelEsimOrderActions';
import { fadeUp } from '@/lib/animations';
import { formatDateShort, formatDateTime } from '@/lib/format';
import type { TravelEsimDetail } from '@octave/contracts';
import { motion } from 'framer-motion';
import { Loader2, Mail, Phone, QrCode, Send, User } from 'lucide-react';
import { useState } from 'react';

interface TravelEsimHeroCardProps {
  esim: TravelEsimDetail;
}

/**
 * Hero card of an eSIM: beneficiary, QR code state and actions, key
 * figures (number, plans, creation, billing center). The plans themselves
 * are listed below the card.
 */
export function TravelEsimHeroCard({ esim }: TravelEsimHeroCardProps) {
  const [showQrCode, setShowQrCode] = useState(false);
  const resend = useResendTravelEsimQrCode(esim.id);
  const sent = !!esim.qrCodeSentAt;
  const { beneficiary } = esim;

  return (
    <motion.div
      variants={fadeUp}
      className="rounded-2xl bg-white p-4 sm:p-8 shadow-sm"
    >
      <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
        <div>
          <div className="flex flex-wrap items-center gap-2 sm:gap-4">
            <h1 className="text-2xl sm:text-4xl font-bold tracking-tight text-slate-900">
              {beneficiary.name}
            </h1>
            <span
              className={`inline-flex items-center gap-1.5 rounded-full border px-3 py-1 text-xs font-semibold ${
                sent
                  ? 'bg-emerald-50 text-emerald-700 border-emerald-200'
                  : 'bg-amber-50 text-amber-700 border-amber-200'
              }`}
            >
              {sent ? 'QR code envoyé' : 'QR code en attente'}
            </span>
          </div>
          <div className="mt-2 flex flex-wrap items-center gap-x-4 gap-y-1 text-sm text-slate-400">
            <span className="flex items-center gap-1.5">
              <User className="h-3.5 w-3.5" />
              {beneficiary.kind === 'LINE' ? 'Membre de la flotte' : 'Hors flotte'}
            </span>
            {beneficiary.kind === 'LINE' && (
              <span className="flex items-center gap-1.5">
                <Phone className="h-3.5 w-3.5" />
                {beneficiary.lineNumber}
              </span>
            )}
            <span className="flex items-center gap-1.5">
              <Mail className="h-3.5 w-3.5" />
              {beneficiary.email}
            </span>
          </div>
        </div>

        {sent && (
          <div className="flex shrink-0 flex-wrap items-center gap-2">
            <PermissionGuard permission="write">
              <button
                type="button"
                onClick={() => resend.mutate()}
                disabled={resend.isPending}
                className="inline-flex items-center gap-2 rounded-xl bg-slate-50 px-4 py-2 text-sm font-medium text-slate-600 transition-colors hover:bg-slate-100 disabled:opacity-60 cursor-pointer"
              >
                {resend.isPending ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <Send className="h-4 w-4" />
                )}
                Renvoyer par e-mail
              </button>
            </PermissionGuard>
            <button
              type="button"
              onClick={() => setShowQrCode(true)}
              className="inline-flex items-center gap-2 rounded-full bg-emerald-600 px-4 py-2 text-sm font-medium text-white shadow-sm transition-all hover:bg-emerald-700 hover:shadow-md cursor-pointer"
            >
              <QrCode className="h-4 w-4" />
              Voir le QR code
            </button>
          </div>
        )}
      </div>

      {/* Key info strip */}
      <div className="mt-4 sm:mt-8 grid grid-cols-2 gap-3 sm:grid-cols-4">
        <div className="rounded-xl bg-slate-50 px-4 py-3">
          <p className="text-xs text-slate-400">Numéro eSIM</p>
          <p className="mt-0.5 text-sm font-semibold text-slate-900 font-mono truncate">
            {esim.esimNumber ?? 'À venir'}
          </p>
        </div>
        <div className="rounded-xl bg-slate-50 px-4 py-3">
          <p className="text-xs text-slate-400">Forfaits chargés</p>
          <p className="mt-0.5 text-sm font-semibold text-slate-900">
            {esim.plansCount}
          </p>
        </div>
        <div className="rounded-xl bg-slate-50 px-4 py-3">
          <p className="text-xs text-slate-400">QR code</p>
          <p className="mt-0.5 text-sm font-semibold text-slate-900">
            {esim.qrCodeSentAt
              ? `Envoyé le ${formatDateTime(esim.qrCodeSentAt)}`
              : 'En attente'}
          </p>
        </div>
        <div className="rounded-xl bg-slate-50 px-4 py-3">
          <p className="text-xs text-slate-400">Créée le</p>
          <p className="mt-0.5 text-sm font-semibold text-slate-900">
            {formatDateShort(esim.createdAt)}
          </p>
          {esim.siteName && (
            <p className="text-xs text-slate-400 truncate">{esim.siteName}</p>
          )}
        </div>
      </div>

      {showQrCode && (
        <TravelEsimQrCodeDialog
          esimId={esim.id}
          title={`eSIM de ${beneficiary.name}`}
          onClose={() => setShowQrCode(false)}
        />
      )}
    </motion.div>
  );
}
